/**
 * V3UploadEntryCard — Stage C.1 calm navigation entry (May 26, 2026).
 *
 * Operator scope:
 *   ONE small persona-aware entry tile/link that lands the user
 *   inside the existing thin UploadShell at:
 *     • learner  → /learner/upload
 *     • adi      → /instructor/upload
 *     • pdi      → /pdi/upload
 *     • admin    → /instructor/upload   (operator-default)
 *
 * Doctrine pins (operator brief verbatim):
 *   • Persona boundaries are absolute: learner never sees ADI/PDI
 *     options; ADI/PDI never see learner-only wording.
 *   • No new upload pipeline. The card is pure navigation; every
 *     byte still flows through /api/v3/media/* via UploadShell.
 *   • No dashboard expansion. Single card, one verb.
 *   • Trusted-invite banner is the responsibility of the shell;
 *     this card is just the doorway.
 *   • Wording obeys COMMUNICATION_DOCTRINE_BIBLE.md §7:
 *     no SaaS jargon, no manufactured urgency.
 *
 * Rendering rules:
 *   • Returns null for any role outside the persona table — the
 *     surface stays calm rather than showing a broken link.
 */
import { Link } from "react-router-dom";
import { UploadCloud, ChevronRight } from "lucide-react";
import { resolveEffectiveRole } from "../utils/roleResolver";

const ONE_FRONT_DOOR = {
  testid: "one-front-door-entry",
  path: "/import-todays-recordings",
  eyebrow: "Today's evidence",
  headline: "Import today's recordings",
  sub: "One place — whatever you upload, I'll sort it out from there.",
};

// 2026-07-25 · GC60 One-Front-Door corrective programme:
// Every persona now lands on the SAME entry — the Founder never picks a
// role-specific upload path any more. The system decides what happens next.
const ENTRY_BY_ROLE = {
  learner: ONE_FRONT_DOOR,
  adi:     ONE_FRONT_DOOR,
  pdi:     ONE_FRONT_DOOR,
  admin:   ONE_FRONT_DOOR,
};

/**
 * V3UploadEntryCard
 *
 * Persona-aware doorway into the thin UploadShell. The component
 * resolves the user's EFFECTIVE role via the central
 * `resolveEffectiveRole` helper — this is what makes admin-in-
 * REAL-MODE-ADI land on `/instructor/upload` AND have the backend
 * persona resolution agree.
 *
 * Accepts EITHER `user` (preferred — single source of truth) OR
 * the legacy `role` prop (kept for transitional compatibility
 * with existing callers). When both are provided, `user` wins.
 */
export default function V3UploadEntryCard({ user, role }) {
  const effective = user
    ? resolveEffectiveRole(user)
    : (role || "").toLowerCase();
  const entry = ENTRY_BY_ROLE[effective];
  if (!entry) return null;

  return (
    <Link
      to={entry.path}
      data-testid={entry.testid}
      data-v3-upload-entry-role={effective}
      className="
        block w-full text-left rounded-2xl
        border-2 border-emerald-300 dark:border-emerald-800
        bg-gradient-to-br from-emerald-50 to-teal-50
        dark:from-emerald-900/20 dark:to-teal-900/20
        p-5
        hover:border-emerald-400 dark:hover:border-emerald-600
        transition-colors
      "
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
          <UploadCloud className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700 dark:text-emerald-300">
            {entry.eyebrow}
          </p>
          <p className="text-base font-extrabold text-slate-900 dark:text-white mt-1">
            {entry.headline}
          </p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
            {entry.sub}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-2" />
      </div>
    </Link>
  );
}
